import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Fab, Tooltip } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import {
  voiceButtonIdleGradient,
  voiceClusterMotionSx,
  voiceClusterPhaseColors,
  voiceClusterPhaseFill,
  voiceClusterReducedMotionSx,
  voiceDialogPhaseColor,
} from "./aiAssist/animations";
import { VoiceDialogIcon } from "./drawer/drawerChrome";
import { TOWER_ASSISTANT_FAB_SIZE_PX } from "./TowerAssistantFab";
import type { VoiceDialogPhase } from "./voiceLaunch";
import VoicePhaseHint from "./VoicePhaseHint";

/** Mic circle, same size as the idle assistant FAB. */
const BUTTON_PX = TOWER_ASSISTANT_FAB_SIZE_PX;
const MIC_ICON_PX = 28;
const CLOSE_ICON_PX = 16;
const ASSISTANT_ICON_PX = 24;
const SLIDE_MS = 220;

export const VOICE_CLOSE_BUTTON_PX = 40;
const VOICE_ASSISTANT_BUTTON_PX = 56;
export const VOICE_LAUNCH_GAP_PX = 8;

type VoiceButtonIcon = "mic" | "close" | "assistant";

function buttonSizePx(icon: VoiceButtonIcon): number {
  if (icon === "close") {
    return VOICE_CLOSE_BUTTON_PX;
  }
  if (icon === "assistant") {
    return VOICE_ASSISTANT_BUTTON_PX;
  }
  return BUTTON_PX;
}

/** Per-variant Fab styling, split out so the component body stays readable. */
function voiceButtonFabSx(
  theme: Theme,
  icon: VoiceButtonIcon,
  phase: VoiceDialogPhase,
  sizePx: number,
) {
  const assist = theme.app.aiAssist;
  const motion = voiceClusterMotionSx(assist.gradientStart);
  const reduced = voiceClusterReducedMotionSx();
  const circle = {
    width: sizePx,
    height: sizePx,
    minWidth: sizePx,
    minHeight: sizePx,
    p: 0,
    position: "relative" as const,
    borderRadius: "50%",
  };
  if (icon === "close") {
    const dark = theme.palette.mode === "dark";
    const borderColor = dark ? assist.avatarBg : assist.border;
    const hoverBg = dark ? assist.avatarBg : assist.raisedBg;
    return {
      ...circle,
      boxSizing: "border-box",
      bgcolor: assist.cardBg,
      backgroundImage: "none",
      border: "1px solid",
      borderColor,
      boxShadow: assist.bubbleShadow,
      color: assist.textStrong,
      ...motion,
      "&:hover": {
        bgcolor: hoverBg,
        borderColor,
        boxShadow: assist.bubbleShadow,
        color: assist.textTitle,
      },
      "&.Mui-disabled": {
        bgcolor: assist.cardBg,
        borderColor,
        color: assist.textStrong,
        boxShadow: assist.bubbleShadow,
        opacity: 0.6,
      },
      ...reduced,
    };
  }
  if (icon === "assistant") {
    const gradient = `linear-gradient(135deg, ${assist.gradientStart}, ${assist.gradientEnd})`;
    return {
      ...circle,
      overflow: "visible",
      border: 0,
      bgcolor: "transparent",
      backgroundImage: gradient,
      boxShadow: assist.fabShadow,
      color: assist.onAccent,
      ...motion,
      "&:hover": {
        bgcolor: "transparent",
        backgroundImage: gradient,
        boxShadow: assist.fabShadowHover,
        color: assist.onAccent,
      },
      "&.Mui-disabled": {
        bgcolor: "transparent",
        backgroundImage: gradient,
        color: assist.onAccent,
        boxShadow: assist.fabShadow,
        opacity: 0.6,
      },
      ...reduced,
    };
  }
  const idleFill = {
    bgcolor: "transparent",
    backgroundImage: voiceButtonIdleGradient(assist),
  };
  const fill =
    phase === "idle"
      ? idleFill
      : {
          bgcolor: voiceDialogPhaseColor(assist, phase),
          backgroundImage: "none",
        };
  return {
    ...circle,
    boxShadow: assist.fabShadow,
    color: assist.onAccent,
    overflow: "hidden",
    ...fill,
    "&:hover": {
      boxShadow: assist.fabShadowHover,
      color: assist.onAccent,
      ...fill,
    },
    "&.Mui-disabled": {
      color: assist.onAccent,
      boxShadow: assist.fabShadow,
      ...idleFill,
      opacity: 0.6,
    },
  };
}

/** The glyph inside the Fab: close X, assistant sparkle + phase dot, or the mic. */
function VoiceButtonGlyph({
  icon,
  phase,
}: Readonly<{ icon: VoiceButtonIcon; phase: VoiceDialogPhase }>) {
  if (icon === "close") {
    return (
      <CloseIcon
        sx={{
          position: "relative",
          zIndex: 1,
          width: CLOSE_ICON_PX,
          height: CLOSE_ICON_PX,
          fontSize: CLOSE_ICON_PX,
        }}
      />
    );
  }
  if (icon === "mic") {
    return <VoiceDialogIcon sizePx={MIC_ICON_PX} />;
  }
  return (
    <>
      <AutoAwesomeIcon
        sx={{
          position: "relative",
          zIndex: 1,
          width: ASSISTANT_ICON_PX,
          height: ASSISTANT_ICON_PX,
          fontSize: ASSISTANT_ICON_PX,
        }}
      />
      {phase !== "idle" ? (
        <Box
          aria-hidden
          sx={(theme) => ({
            position: "absolute",
            top: 2,
            right: 2,
            zIndex: 2,
            width: 14,
            height: 14,
            boxSizing: "border-box",
            border: "2.5px solid",
            borderColor:
              theme.palette.mode === "dark"
                ? theme.palette.background.default
                : theme.palette.background.paper,
            borderRadius: "50%",
            pointerEvents: "none",
            bgcolor: voiceClusterPhaseFill(voiceClusterPhaseColors(theme), phase),
          })}
        />
      ) : null}
    </>
  );
}

/**
 * Circular control stacked with the assistant FAB.
 * Idle is the waveform on the Munis-pill gradient. During a dialog this slot
 * is a neutral close button, and a second instance reopens the drawer on the
 * assistant gradient with a phase dot.
 */
export default function TowerAssistantVoiceButton({
  revealed,
  floating,
  phase,
  disabled,
  tooltip,
  onClick,
  icon = "mic",
  label = "Voice input",
  offsetPx = 0,
}: Readonly<{
  revealed: boolean;
  /** Absolute, above the FAB. When false the button sits in the FAB's corner. */
  floating: boolean;
  phase: VoiceDialogPhase;
  disabled: boolean;
  tooltip: string;
  onClick: () => void;
  /** Close ends the dialog. Assistant reopens the drawer. The mic starts a dialog. */
  icon?: VoiceButtonIcon;
  label?: string;
  /** Extra distance down from the top of the cluster when several buttons stack. */
  offsetPx?: number;
}>) {
  const sizePx = buttonSizePx(icon);
  const slidePx = sizePx + VOICE_LAUNCH_GAP_PX;
  const shown = floating ? "translateX(-50%) translateY(0)" : undefined;
  const hidden = floating ? `translateX(-50%) translateY(${slidePx}px)` : undefined;

  const control = (
    <Box
      component="span"
      aria-hidden={revealed ? undefined : true}
      sx={{
        display: "inline-flex",
        lineHeight: 0,
        position: "relative",
        overflow: "visible",
        ...(floating
          ? {
              position: "absolute",
              top: offsetPx,
              left: "50%",
              transform: revealed ? shown : hidden,
            }
          : {}),
        opacity: revealed ? 1 : 0,
        // Leave hits to the parent while revealed. An explicit `auto` would
        // stay clickable after compact chrome sets the cluster to `none`.
        pointerEvents: revealed ? undefined : "none",
        transition: `opacity ${SLIDE_MS}ms ease, transform ${SLIDE_MS}ms ease`,
        "@media (prefers-reduced-motion: reduce)": {
          transform: floating ? "translateX(-50%)" : undefined,
          transition: "opacity 0.2s ease",
        },
      }}
    >
      {icon === "mic" ? <VoicePhaseHint phase={phase} shown={revealed} /> : null}
      <Fab
        type="button"
        aria-label={label}
        aria-pressed={icon === "mic" && phase !== "idle"}
        data-voice-phase={phase}
        disabled={disabled}
        tabIndex={revealed ? 0 : -1}
        onClick={onClick}
        sx={(theme) => voiceButtonFabSx(theme, icon, phase, sizePx)}
      >
        <VoiceButtonGlyph icon={icon} phase={phase} />
      </Fab>
    </Box>
  );

  if (icon === "mic") {
    return control;
  }
  return <Tooltip title={tooltip}>{control}</Tooltip>;
}
